import { Box, Stack, Switch, Typography } from '@mui/material';
import { toggleRowStyles } from '@pages/CustomPoiTypes.styles';
import { useId } from 'react';

interface Props {
  label: string;
  /**
   * One sentence on what turning this on actually does on the device. Optional because a
   * previewed field carries whatever help its author wrote, which is often nothing.
   */
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * A single on/off option, with room for the sentence that explains it. The switch is named by
 * the label and described by the sentence, so that a screen reader says what it turns on.
 */
const ToggleRow: React.FC<Props> = ({ label, description, checked, onChange, disabled }) => {
  const labelId = useId();
  const descriptionId = useId();

  return (
    <Stack direction="row" sx={toggleRowStyles}>
      <Box sx={{ minWidth: 0 }}>
        <Typography id={labelId} variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        {description && (
          <Typography id={descriptionId} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_event, value) => onChange(value)}
        slotProps={{
          input: {
            'aria-labelledby': labelId,
            ...(description ? { 'aria-describedby': descriptionId } : {}),
          } as React.InputHTMLAttributes<HTMLInputElement>,
        }}
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  );
};

export default ToggleRow;
